import React, { useEffect, useMemo, useState } from "react";
import { flanges, spool, spoolSurveys } from "../lib/spool";
import { jobs, station } from "../lib/api";
import { fmt } from "../lib/api";
import { toast } from "sonner";
import { Target, Crosshair, Trash, CheckCircle, Cube, Plus, FolderOpen, CaretLeft } from "@phosphor-icons/react";
import { FlangeView } from "./FlangeView";
import { Flange3D } from "./Flange3D";
import { Flange2D } from "./Flange2D";

/**
 * Spool Survey mode — pick a flange from the ANSI/BS/HUB library, then hit
 * MEASURE around its circumference. The panel shows a live radial diagram
 * of coverage plus fitted diameter vs nominal.
 */

// Top-level router: shows the list of Spool Surveys with a "NEW" button; when
// the surveyor opens one, the flange-survey detail view is rendered.
export const SpoolPanel = ({ disabled }) => {
  const [surveys, setSurveys] = useState([]);
  const [active, setActive] = useState(null); // SpoolSurvey doc
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    spoolSurveys.list()
      .then((rows) => { setSurveys(rows); setLoading(false); })
      .catch(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const openSurvey = async (id) => {
    try {
      const s = await spoolSurveys.get(id);
      setActive(s);
    } catch (e) {
      toast.error("Failed to open spool survey");
    }
  };

  const createSurvey = async (payload) => {
    try {
      const s = await spoolSurveys.create(payload);
      setShowNew(false);
      refresh();
      setActive(s);
      toast.success(`Spool Survey ${s.project_number}-${s.spool_name}#${s.spool_number} created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create spool survey");
    }
  };

  const removeSurvey = async (id) => {
    if (!window.confirm("Delete this spool survey and all its flanges?")) return;
    try {
      await spoolSurveys.remove(id);
      toast.success("Spool survey deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const closeSurvey = () => { setActive(null); refresh(); };

  if (active) {
    return (
      <SpoolSurveyDetail
        disabled={disabled}
        survey={active}
        onBack={closeSurvey}
        onSurveyChange={setActive}
      />
    );
  }
  return (
    <>
      <SpoolSurveyList
        surveys={surveys}
        loading={loading}
        onOpen={openSurvey}
        onNew={() => setShowNew(true)}
        onDelete={removeSurvey}
      />
      {showNew && (
        <NewSpoolSurveyDialog
          onClose={() => setShowNew(false)}
          onCreate={createSurvey}
        />
      )}
    </>
  );
};

// ---------- List view ----------
//
// Shown as the SPOOL tab landing page. Each row is a Spool Survey doc; clicking
// one opens the flange-survey detail view. `NEW SPOOL SURVEY` opens a dialog
// to capture project#, spool name, spool# etc. and then jumps straight into
// the freshly-created survey.
const SpoolSurveyList = ({ surveys, loading, onOpen, onNew, onDelete }) => {
  return (
    <div className="panel" data-testid="spool-survey-list-panel">
      <div className="panel-header">
        <span>SPOOL SURVEYS</span>
        <span className="text-[10px] tracking-widest text-[var(--text-mute)]">
          {surveys.length} total
        </span>
      </div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-xs text-[var(--text-dim)]">
          Each spool survey holds many flanges / TPs. Open one to continue, or
          start a new one.
        </div>
        <button
          data-testid="new-spool-survey-btn"
          onClick={onNew}
          className="btn-tac !min-h-[40px] px-4 text-sm"
          data-variant="primary"
        >
          <span className="flex items-center gap-1">
            <Plus size={16} weight="bold" /> NEW SPOOL SURVEY
          </span>
        </button>
      </div>

      {loading ? (
        <div className="rounded-sm border border-dashed border-[var(--border)] p-6 text-center text-xs text-[var(--text-mute)]">
          Loading…
        </div>
      ) : surveys.length === 0 ? (
        <div
          data-testid="spool-survey-empty"
          className="rounded-sm border border-dashed border-[var(--border)] bg-[var(--surface-2)] p-6 text-center text-xs text-[var(--text-mute)]"
        >
          No spool surveys yet. Click <b>NEW SPOOL SURVEY</b> to start one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-sm border border-[var(--border)]">
          <table className="w-full text-xs">
            <thead className="bg-[var(--surface-2)] text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
              <tr>
                <th className="px-3 py-2 text-left font-normal">Project</th>
                <th className="px-2 py-2 text-left font-normal">Spool</th>
                <th className="px-2 py-2 text-left font-normal">TPs</th>
                <th className="px-2 py-2 text-left font-normal">Client</th>
                <th className="px-2 py-2 text-left font-normal">Status</th>
                <th className="px-2 py-2 text-right font-normal">Created</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`spool-survey-row-${s.id}`}
                  className="border-t border-[var(--border)] hover:bg-[var(--surface-2)]"
                >
                  <td
                    className="cursor-pointer px-3 py-2 font-semibold text-[var(--text)]"
                    onClick={() => onOpen(s.id)}
                  >
                    {s.project_number}
                  </td>
                  <td className="cursor-pointer px-2 py-2 text-[var(--text)]"
                      onClick={() => onOpen(s.id)}>
                    <span className="font-semibold">{s.spool_name}</span>
                    <span className="ml-1 text-[var(--text-dim)]">#{s.spool_number}</span>
                  </td>
                  <td className="px-2 py-2 readout tabular-nums text-[var(--text-dim)]">
                    {s.last_flange_number || 0}
                  </td>
                  <td className="px-2 py-2 text-[var(--text-dim)]">
                    {s.client || "—"}
                  </td>
                  <td className="px-2 py-2 text-[10px] uppercase tracking-widest">
                    {s.status === "complete" ? (
                      <span className="text-[var(--neon)]">✓ complete</span>
                    ) : (
                      <span className="text-[var(--amber)]">in progress</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right text-[10px] text-[var(--text-mute)]">
                    {new Date(s.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button
                      data-testid={`spool-survey-delete-${s.id}`}
                      onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                      className="text-[var(--text-mute)] hover:text-[var(--danger)]"
                      title="Delete"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------- New Spool Survey dialog ----------

const NewSpoolSurveyDialog = ({ onClose, onCreate }) => {
  const [projectNumber, setProjectNumber] = useState("");
  const [spoolName, setSpoolName] = useState("");
  const [spoolNumber, setSpoolNumber] = useState("");
  const [client, setClient] = useState("");
  const [site, setSite] = useState("");
  const [notes, setNotes] = useState("");

  const canCreate = projectNumber.trim() && spoolName.trim() && spoolNumber.trim();
  const submit = (e) => {
    e?.preventDefault();
    if (!canCreate) return;
    onCreate({
      project_number: projectNumber.trim(),
      spool_name: spoolName.trim(),
      spool_number: spoolNumber.trim(),
      client: client.trim() || null,
      site: site.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <div
      data-testid="new-spool-survey-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-md border border-[var(--neon)] bg-[var(--surface)] shadow-[0_0_30px_rgba(0,255,153,0.15)]"
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div className="flex items-center gap-2">
            <FolderOpen size={18} weight="bold" className="text-[var(--neon)]" />
            <span className="text-sm font-bold tracking-widest">NEW SPOOL SURVEY</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--text-mute)] hover:text-[var(--text)]"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
          <FieldInput
            testid="new-spool-project"
            label="Project number *"
            value={projectNumber}
            onChange={setProjectNumber}
            placeholder="e.g. P-1234"
            autoFocus
          />
          <FieldInput
            testid="new-spool-name"
            label="Spool name *"
            value={spoolName}
            onChange={setSpoolName}
            placeholder="e.g. L-100"
          />
          <FieldInput
            testid="new-spool-number"
            label="Spool number *"
            value={spoolNumber}
            onChange={setSpoolNumber}
            placeholder="e.g. 01"
          />
          <FieldInput
            testid="new-spool-client"
            label="Client"
            value={client}
            onChange={setClient}
            placeholder="optional"
          />
          <FieldInput
            testid="new-spool-site"
            label="Site / location"
            value={site}
            onChange={setSite}
            placeholder="optional"
          />
          <FieldInput
            testid="new-spool-notes"
            label="Notes"
            value={notes}
            onChange={setNotes}
            placeholder="optional"
          />
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-4 py-3">
          <span className="text-[10px] uppercase tracking-widest text-[var(--text-mute)]">
            * required
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-tac !min-h-[40px] px-4 text-sm"
            >
              CANCEL
            </button>
            <button
              type="submit"
              data-testid="new-spool-create-btn"
              disabled={!canCreate}
              className="btn-tac !min-h-[40px] px-4 text-sm"
              data-variant="primary"
            >
              CREATE & OPEN
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const FieldInput = ({ testid, label, value, onChange, placeholder, autoFocus }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
      {label}
    </span>
    <input
      data-testid={testid}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
    />
  </label>
);

// ---------- Detail view (was SpoolPanel) ----------

const SpoolSurveyDetail = ({ disabled, survey, onBack, onSurveyChange }) => {
  const [standards, setStandards] = useState([]);
  const [selStd, setSelStd] = useState("");
  const [selSize, setSelSize] = useState("");
  const [selClass, setSelClass] = useState("");
  const [selGasket, setSelGasket] = useState("");
  const [matches, setMatches] = useState([]);
  const [flange, setFlange] = useState(null);

  const [tag, setTag] = useState("");
  const [flangeNumber, setFlangeNumber] = useState(1);
  const [offsetAxial, setOffsetAxial] = useState("0");
  const [offsetRadial, setOffsetRadial] = useState("0");
  const [session, setSession] = useState(null);
  const [live, setLive] = useState(null);
  const [measuring, setMeasuring] = useState(false);
  const [kind, setKind] = useState("face1"); // face1 | face2 | face3 | bolt
  // Cross-flange history accumulated during one continuous SURVEY run.
  // Each entry is a snapshot of a finalized session so the ShotList can keep
  // showing every point from spool #1 → #N without the surveyor leaving the
  // live view.
  const [runHistory, setRunHistory] = useState(survey.run_history || []);
  // When set, we render a modal asking the surveyor to confirm the next
  // flange's design + TP number without leaving the survey page.
  const [nextFlangeDraft, setNextFlangeDraft] = useState(null);
  // Points already saved in this survey's backing job — refreshed after each
  // FINALIZE so the "Surveyed flanges" list stays in sync.
  const [jobPoints, setJobPoints] = useState([]);
  const job = { id: survey.job_id, name: `${survey.project_number}-${survey.spool_name}#${survey.spool_number}` };

  // Load points on mount + when survey.job_id changes.
  useEffect(() => {
    jobs.points(survey.job_id).then(setJobPoints).catch(() => setJobPoints([]));
  }, [survey.job_id]);

  // Init the next flange number from the survey's last recorded number.
  useEffect(() => {
    if (survey.last_flange_number > 0) {
      setFlangeNumber(survey.last_flange_number + 1);
    }
  }, [survey.last_flange_number]);

  // Load standards on mount
  useEffect(() => {
    flanges.standards().then(setStandards).catch(() => {});
  }, []);

  // Refresh matches when filters change
  useEffect(() => {
    if (!selStd) {
      setMatches([]);
      return;
    }
    const params = { standard: selStd };
    if (selSize) params.size = selSize;
    if (selClass) params.klass = selClass;
    if (selGasket) params.gasket = selGasket;
    flanges.list(params).then((rows) => {
      setMatches(rows);
      if (rows.length === 1) setFlange(rows[0]);
      else setFlange(null);
    }).catch(() => setMatches([]));
  }, [selStd, selSize, selClass, selGasket]);

  const uniq = (key) =>
    Array.from(new Set(matches.map((f) => f[key]).filter(Boolean))).sort();
  const uniqAll = (key) => {
    // From current standard, regardless of other filters
    if (!selStd) return [];
    const src = matches.length ? matches : [];
    return Array.from(new Set(src.map((f) => f[key]).filter(Boolean)));
  };

  const sizes = useMemo(() => {
    return Array.from(
      new Set(
        matches
          .concat(flange ? [flange] : [])
          .map((f) => f.size)
          .filter(Boolean)
      )
    ).sort((a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b));
  }, [matches, flange]);

  const classes = useMemo(() => {
    return uniqAll("class").sort((a, b) => {
      const na = parseInt(a); const nb = parseInt(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
  }, [matches, selStd]);

  const gaskets = useMemo(() => uniqAll("gasket"), [matches, selStd]);

  const startSession = async () => {
    if (!flange) return;
    try {
      const s = await spool.createSession({
        flange_id: flange.id,
        tag: tag || null,
        flange_number: Number(flangeNumber) || 1,
        offset_axial_mm: parseFloat(offsetAxial) || 0,
        offset_radial_mm: parseFloat(offsetRadial) || 0,
      });
      setSession(s);
      setLive(null);
      toast.success(`Flange ${flangeNumber} · ${flange.standard} ${flange.size}" cl.${flange.class}`);
    } catch (e) {
      toast.error("Failed to start session");
    }
  };

  const measure = async () => {
    if (!session) return;
    setMeasuring(true);
    try {
      // Real-hardware path: fire the bridge to move the head + trigger
      // the EDM, then post the actual x/y/z back to the spool backend.
      // If the bridge is offline / sim-mode the returned coord is null
      // (or has null x/y/z) and the backend falls back to its virtual-
      // flange simulator so the demo still works.
      let coord = null;
      try {
        const shot = await station.measure("coord");
        if (shot && shot.x !== null && shot.x !== undefined) {
          coord = {
            x: shot.x, y: shot.y, z: shot.z,
            hz: shot.hz, v: shot.v, sd: shot.sd,
          };
        } else if (shot && shot.error) {
          toast.error(`Total station: ${shot.error}`);
          return;
        }
      } catch (e) {
        // Bridge unreachable — fall through to backend simulator so the
        // demo still works when the user is not connected to hardware.
        console.warn("Bridge measure failed, using backend simulator:", e);
      }
      const r = await spool.measure(session.id, undefined, kind, coord);
      setLive(r);
      const label = KIND_META[kind].label;
      const n = r[kind].n_points;
      toast.success(`${label} point ${n} captured`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Measurement failed");
    } finally {
      setMeasuring(false);
    }
  };

  const removePoint = async (idx) => {
    if (!session) return;
    try {
      const r = await spool.deletePoint(session.id, idx);
      setLive(r);
    } catch (e) { toast.error("Delete failed"); }
  };

  const finalize = async (force = false) => {
    if (!session || !job) return;
    try {
      const res = await spool.finalize(session.id, job.id, force);
      toast.success(`Flange #${session.flange_number} finalized → saved to ${job.name}`);
      const snapshot = {
        flangeNumber: session.flange_number || 1,
        tag: session.tag || "",
        flange,
        points: (live?.points || []).slice(),
        fitted: res,
      };
      const newHistory = [...runHistory, snapshot];
      setRunHistory(newHistory);
      // Persist to the SpoolSurvey document so this can be resumed later.
      const patched = await spoolSurveys.update(survey.id, {
        run_history: newHistory,
        last_flange_number: snapshot.flangeNumber,
      }).catch(() => null);
      if (patched) onSurveyChange?.(patched);
      // Refresh the surveyed-flanges list from the backing job.
      jobs.points(survey.job_id).then(setJobPoints).catch(() => {});
      // Pre-fill the next-flange draft with the same standard/class/gasket so
      // the surveyor just confirms and hits START.
      setNextFlangeDraft({
        justFinishedNumber: session.flange_number || 1,
        selStd, selSize, selClass, selGasket,
        flangeNumber: (session.flange_number || 1) + 1,
        tag: "",
        offsetAxial: offsetAxial || "0",
        offsetRadial: offsetRadial || "0",
        flangeId: flange?.id || null,
      });
      setSession(null); setLive(null); setKind("face1");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalize failed");
    }
  };

  const finishSurvey = async () => {
    setNextFlangeDraft(null);
    try {
      await spoolSurveys.update(survey.id, { status: "complete" });
      toast.success(`Spool Survey ${job.name} complete`);
    } catch (e) {
      // non-fatal — we still exit even if the patch fails
    }
    // Bounce back to the survey list.
    onBack?.();
  };

  // Confirm a queued next-flange draft and immediately open a new session so
  // the surveyor stays on the live view (no picker step in the middle).
  const confirmNextFlange = async (draft) => {
    if (!draft?.flangeId) {
      toast.error("Pick a flange first");
      return;
    }
    try {
      const s = await spool.createSession({
        flange_id: draft.flangeId,
        tag: draft.tag || null,
        flange_number: Number(draft.flangeNumber) || 1,
        offset_axial_mm: parseFloat(draft.offsetAxial) || 0,
        offset_radial_mm: parseFloat(draft.offsetRadial) || 0,
      });
      setSession(s);
      setLive(null);
      setKind("face1");
      // Push the confirmed values back into the top-level filters so they stay
      // consistent if the surveyor later dismisses the survey run.
      setSelStd(draft.selStd);
      setSelSize(draft.selSize);
      setSelClass(draft.selClass);
      setSelGasket(draft.selGasket);
      setTag(draft.tag);
      setFlangeNumber(draft.flangeNumber);
      setOffsetAxial(draft.offsetAxial);
      setOffsetRadial(draft.offsetRadial);
      setNextFlangeDraft(null);
      toast.success(`Flange #${draft.flangeNumber} started`);
    } catch (e) {
      toast.error("Failed to start next flange");
    }
  };

  const cancel = () => {
    setSession(null); setLive(null); setTag("");
  };

  return (
    <div className="panel" data-testid="spool-panel">
      <div className="panel-header">
        <div className="flex min-w-0 items-center gap-2">
          <button
            data-testid="back-to-surveys-btn"
            onClick={onBack}
            className="btn-tac !min-h-[28px] px-2 text-[10px]"
            title="Back to spool surveys"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <span className="min-w-0 truncate">
            <span className="text-[var(--text)]">{survey.project_number}</span>
            <span className="text-[var(--text-dim)]"> · {survey.spool_name} #{survey.spool_number}</span>
          </span>
        </div>
        {session ? (
          <span className="text-[10px] tracking-widest text-[var(--neon)] glow-green">
            SESSION · {session.id.slice(0, 8)}
          </span>
        ) : nextFlangeDraft ? (
          <span className="text-[10px] tracking-widest text-[var(--amber)] glow-amber">
            RUN · {runHistory.length} DONE · WAITING
          </span>
        ) : (
          <span className="text-[10px] tracking-widest text-[var(--text-mute)]">
            {runHistory.length ? `${runHistory.length} FLANGES · SELECT NEXT` : "SELECT FLANGE"}
          </span>
        )}
      </div>

      {(session || nextFlangeDraft) ? (
        <>
          <SpoolLive
            session={session}
            flange={session ? flange : (runHistory[runHistory.length - 1]?.flange || null)}
            live={live}
            measuring={measuring}
            kind={kind}
            setKind={setKind}
            onMeasure={measure}
            onRemovePoint={removePoint}
            onFinalize={finalize}
            onCancel={cancel}
            onFinishRun={finishSurvey}
            disabled={disabled}
            job={job}
            history={runHistory}
            waitingForNext={!session && !!nextFlangeDraft}
          />
          {nextFlangeDraft && (
            <NextFlangeDialog
              draft={nextFlangeDraft}
              standards={standards}
              onChange={setNextFlangeDraft}
              onConfirm={confirmNextFlange}
              onFinishRun={finishSurvey}
            />
          )}
        </>
      ) : (
        <>
          <SurveyedFlanges jobPoints={jobPoints} job={job} />
          <FlangePicker
            standards={standards}
            selStd={selStd} setSelStd={setSelStd}
            sizes={sizes} selSize={selSize} setSelSize={setSelSize}
            classes={classes} selClass={selClass} setSelClass={setSelClass}
            gaskets={gaskets} selGasket={selGasket} setSelGasket={setSelGasket}
            matches={matches} flange={flange} setFlange={setFlange}
            tag={tag} setTag={setTag}
            flangeNumber={flangeNumber} setFlangeNumber={setFlangeNumber}
            offsetAxial={offsetAxial} setOffsetAxial={setOffsetAxial}
            offsetRadial={offsetRadial} setOffsetRadial={setOffsetRadial}
            onStart={startSession}
            disabled={disabled}
          />
        </>
      )}
    </div>
  );
};

// ---------- Post-finalize prompt ----------
// ---------- Flange picker ----------

const FlangePicker = ({
  standards, selStd, setSelStd, sizes, selSize, setSelSize,
  classes, selClass, setSelClass, gaskets, selGasket, setSelGasket,
  matches, flange, setFlange, tag, setTag,
  flangeNumber, setFlangeNumber, offsetAxial, setOffsetAxial,
  offsetRadial, setOffsetRadial, onStart, disabled,
}) => {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Selector testid="spool-standard-select" label="Standard"
          value={selStd}
          onChange={(v) => { setSelStd(v); setSelSize(""); setSelClass(""); setSelGasket(""); }}
          options={standards} />
        <Selector testid="spool-size-select" label="Size (in)"
          value={selSize} onChange={setSelSize} options={sizes} disabled={!selStd} />
        <Selector testid="spool-class-select" label="Class"
          value={selClass} onChange={setSelClass} options={classes} disabled={!selStd} />
        <Selector testid="spool-gasket-select" label="Gasket"
          value={selGasket} onChange={setSelGasket} options={gaskets} disabled={!selStd} />
      </div>

      {selStd && matches.length > 0 && (
        <div className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)]">
          <div className="border-b border-[var(--border)] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            {matches.length} match{matches.length === 1 ? "" : "es"} — click one to select
          </div>
          <div className="max-h-28 overflow-auto">
            {matches.slice(0, 40).map((f) => {
              const active = flange?.id === f.id;
              return (
                <button key={f.id} data-testid={`flange-option-${f.id}`}
                  onClick={() => setFlange(f)}
                  className={`w-full readout tabular-nums block border-b border-[var(--border)] px-3 py-1.5 text-left text-xs last:border-b-0 hover:bg-[var(--surface)] ${
                    active ? "bg-[var(--surface)] text-[var(--neon)]" : "text-[var(--text)]"
                  }`}>
                  <span className="mr-3 inline-block w-10 text-[var(--text-dim)]">#{f.id}</span>
                  <span className="mr-2">{f.standard}</span>
                  <span className="mr-2">{f.size}"</span>
                  <span className="mr-2">cl.{f.class}</span>
                  <span className="mr-2 text-[var(--amber)]">{f.gasket}</span>
                  <span className="text-[var(--text-dim)]">
                    OD Ø{fmt(f.od, 1)} · RF Ø{fmt(f.rf_dia, 1)} · PCD Ø{fmt(f.pcd, 1)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-6 gap-2">
        <NumSmall label="Flange #" testid="flange-number-input" value={flangeNumber} onChange={setFlangeNumber} step="1" />
        <TxtSmall label="Tag" testid="spool-tag-input" value={tag} onChange={setTag} className="col-span-2" />
        <NumSmall label="Axial offset (mm)" testid="offset-axial-input" value={offsetAxial} onChange={setOffsetAxial} step="0.1" />
        <NumSmall label="Radial offset (mm)" testid="offset-radial-input" value={offsetRadial} onChange={setOffsetRadial} step="0.1" />
        <button data-testid="spool-start-btn" onClick={onStart}
          disabled={disabled || !flange}
          className="btn-tac !min-h-[42px] self-end text-sm" data-variant="primary">
          <span className="flex items-center justify-center gap-1">
            <Target size={16} weight="bold" /> START
          </span>
        </button>
      </div>
    </div>
  );
};

const NumSmall = ({ label, testid, value, onChange, step, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-0.5 block text-[9px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <input data-testid={testid} type="number" step={step || "0.001"} inputMode="decimal"
      value={value} onChange={(e) => onChange(e.target.value)}
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-2 py-1.5 text-sm tabular-nums text-[var(--text)] focus:border-[var(--neon)]" />
  </label>
);
const TxtSmall = ({ label, testid, value, onChange, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-0.5 block text-[9px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      placeholder="P-101"
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-2 py-1.5 text-sm text-[var(--text)] focus:border-[var(--neon)]" />
  </label>
);

// ---------- Surveyed flanges in this job ----------

const SurveyedFlanges = ({ jobPoints, job }) => {
  const surveyed = jobPoints.filter((p) => p.code === "FLANGE" || p.code === "SPOOL");
  if (!job) {
    return (
      <div className="rounded-sm border border-dashed border-[var(--border)] bg-[var(--surface-2)] p-4 text-center text-xs text-[var(--text-mute)]">
        Create or select a job to start surveying flanges.
      </div>
    );
  }
  return (
    <div className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)]" data-testid="surveyed-flanges-list">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
        <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
          Surveyed flanges · {job.name}
        </div>
        <div className="readout tabular-nums text-[10px] text-[var(--neon)]">
          {surveyed.length} completed
        </div>
      </div>
      {surveyed.length === 0 ? (
        <div className="px-3 py-4 text-center text-xs text-[var(--text-mute)]">
          No flanges surveyed yet. Pick a flange below to start.
        </div>
      ) : (
        <div className="max-h-40 overflow-auto">
          {surveyed.map((p) => (
            <div
              key={p.id}
              data-testid={`surveyed-flange-${p.number}`}
              className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-3 py-2 text-xs last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <span className="readout tabular-nums font-bold text-[var(--neon)]">
                  #{p.number}
                </span>
                <div>
                  <div className="text-[var(--text)] readout">
                    {p.description || "(spool)"}
                  </div>
                  <div className="mt-0.5 text-[10px] text-[var(--text-mute)]">
                    {new Date(p.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Selector = ({ testid, label, value, onChange, options, disabled }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
    >
      <option value="">— any —</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </label>
);

// Metadata for the 4 measurement kinds
const KIND_META = {
  face1: { label: "FACE 1", sub: "Raised face (front)", code: 1 },
  face2: { label: "FACE 2", sub: "Middle face", code: 2 },
  face3: { label: "FACE 3", sub: "Back face", code: 3 },
  bolt:  { label: "BOLT HOLES", sub: "PCD circle", code: 4 },
};

// ---------- Live measurement ----------

const SpoolLive = ({
  session, flange, live, measuring, kind, setKind,
  onMeasure, onRemovePoint, onFinalize, onCancel, onFinishRun,
  disabled, job, history = [], waitingForNext = false,
}) => {
  const face1 = live?.face1;
  const face2 = live?.face2;
  const face3 = live?.face3;
  const bolt = live?.bolt;
  const active = live?.[kind];
  const ok = live?.ok_for_finalize;
  const points = live?.points || [];
  const [show3D, setShow3D] = useState(false);

  // Workflow gate:
  //   • ONE primary face (F1/F2/F3) needs ≥5 pts + ≥180° coverage.
  //   • The OTHER TWO faces need only ≥1 confirmation shot each.
  //   • Bolt PCD needs ≥3 pts + ≥180° coverage for its own fit.
  const faces = [face1, face2, face3];
  const primaryFace = faces.find(
    (f) => f && f.n_points >= 5 && f.coverage_deg >= 180
  );
  const facesOk = primaryFace && faces.every((f) => (f?.n_points ?? 0) >= 1);
  const boltOk = bolt && bolt.n_points >= 3 && bolt.coverage_deg >= 180;

  // Human-readable list of remaining requirements to enable FINALIZE.
  const missing = [];
  if (!primaryFace) {
    // Show the best-progressed face so the user knows how far they are.
    const best = ["face1", "face2", "face3"]
      .map((k) => ({ k, blk: live?.[k], label: KIND_META[k].label }))
      .sort((a, b) => (b.blk?.n_points ?? 0) - (a.blk?.n_points ?? 0))[0];
    const n = best.blk?.n_points ?? 0;
    const cov = Math.round(best.blk?.coverage_deg ?? 0);
    missing.push(`primary face: ${best.label} ${n}/5 pts, ${cov}/180°`);
  } else if (!facesOk) {
    // Which of the two secondary faces still need a confirmation shot?
    const need = ["face1", "face2", "face3"]
      .map((k) => ({ k, blk: live?.[k], label: KIND_META[k].label }))
      .filter(({ blk }) => (blk?.n_points ?? 0) < 1)
      .map(({ label }) => label);
    missing.push(`confirm shot on ${need.join(" & ")}`);
  }
  if (!boltOk) {
    const cov = Math.round(bolt?.coverage_deg ?? 0);
    missing.push(`PCD +${Math.max(0, 180 - cov)}° · ${bolt?.n_points ?? 0}/3 pts`);
  }
  if (!job) missing.push("select a job");

  // When we are between flanges (session cancelled/finalized but modal open),
  // fall back to a placeholder so the layout stays intact.
  const currentFlangeNumber = session?.flange_number
    ?? (history.length ? history[history.length - 1].flangeNumber + 1 : 1);
  const currentTag = session?.tag ?? "";
  const between = waitingForNext || !session;
  const entries = buildShotEntries(history, currentFlangeNumber, points);

  return (
    <div
      className="flex flex-col gap-2"
      style={{ height: "calc(100vh - 210px)", minHeight: 560 }}
    >
      {/* Row 1: compact header + mode buttons + MEASURE (always visible) */}
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,auto)_minmax(0,auto)]">
        <div className="min-w-0 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="text-base font-bold tracking-wider">
                  {between ? (
                    <span className="text-[var(--amber)]">
                      WAITING · Flange #{currentFlangeNumber} pending
                    </span>
                  ) : (
                    <>#{currentFlangeNumber} · {currentTag || "(no tag)"}</>
                  )}
                </span>
                <span className="text-xs tracking-widest text-[var(--text-dim)]">
                  {flange?.standard} · {flange?.size}" · cl.{flange?.class} · {flange?.gasket || "—"}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] uppercase tracking-widest">
                <span className={primaryFace ? "text-[var(--neon)]" : "text-[var(--amber)]"}>
                  {primaryFace
                    ? `primary ${KIND_META[
                        primaryFace === face1 ? "face1" :
                        primaryFace === face2 ? "face2" : "face3"
                      ].label} ✓`
                    : "primary face pending"}
                </span>
                <span className={facesOk ? "text-[var(--neon)]" : "text-[var(--amber)]"}>
                  · confirm {faces.filter((f) => (f?.n_points ?? 0) >= 1).length}/3
                </span>
                <span className={boltOk ? "text-[var(--neon)]" : "text-[var(--amber)]"}>
                  · PCD {boltOk ? "OK" : "pending"}
                </span>
                <span
                  className="text-[var(--text-dim)]"
                  data-testid="session-code-prefix"
                  title="Field codes assigned to this flange"
                >
                  · codes {codeFor(currentFlangeNumber, "face1")}
                  /{codeFor(currentFlangeNumber, "face2")}
                  /{codeFor(currentFlangeNumber, "face3")}
                  /{codeFor(currentFlangeNumber, "bolt")}
                </span>
                {history.length > 0 && (
                  <span className="text-[var(--cyan)]">
                    · run {history.length} done
                  </span>
                )}
                {session && (session.offset_axial_mm || session.offset_radial_mm) ? (
                  <span className="text-[var(--cyan)]">
                    · offset ax {session.offset_axial_mm}mm · rad {session.offset_radial_mm}mm
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                data-testid="spool-view-3d-toggle"
                onClick={() => setShow3D((v) => !v)}
                className="btn-tac !min-h-[32px] px-3 text-[10px]"
                data-variant={show3D ? "primary" : undefined}
                title="Toggle 3D preview"
              >
                <span className="flex items-center gap-1">
                  <Cube size={14} weight="bold" />
                  3D {show3D ? "ON" : "OFF"}
                </span>
              </button>
              {session ? (
                <button data-testid="spool-cancel-btn" onClick={onCancel}
                  className="btn-tac !min-h-[32px] px-3 text-[10px]" data-variant="danger">
                  CANCEL
                </button>
              ) : (
                <button data-testid="spool-finish-run-btn" onClick={onFinishRun}
                  className="btn-tac !min-h-[32px] px-3 text-[10px]" data-variant="danger">
                  FINISH RUN
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1 lg:min-w-[380px]">
          {["face1", "face2", "face3", "bolt"].map((k) => (
            <ModeBtn key={k} testid={`spool-mode-${k}`}
              active={kind === k} onClick={() => setKind(k)}
              count={live?.[k]?.n_points ?? 0}
              coverage={live?.[k]?.coverage_deg ?? 0}
              label={KIND_META[k].label} sub={KIND_META[k].sub}
              code={codeFor(currentFlangeNumber, k)} />
          ))}
        </div>
        {/* BIG MEASURE button — always in view, right side of mode picker */}
        <button data-testid="spool-measure-btn" onClick={onMeasure}
          disabled={disabled || measuring || between}
          className="btn-tac !min-h-[64px] px-6 text-base lg:!min-h-[74px]" data-variant="primary">
          <span className="flex flex-col items-center gap-0.5">
            <Crosshair size={22} weight="bold" />
            <span className="text-[10px] tracking-widest">
              {between ? "WAITING…" : measuring ? "…" : `MEASURE ${KIND_META[kind].label}`}
            </span>
            <span className="readout tabular-nums text-[10px] text-[var(--text-dim)]">
              {active?.n_points ?? 0} pts · {Math.round(active?.coverage_deg ?? 0)}°
            </span>
          </span>
        </button>
      </div>

      {/* Row 2: shot list (left) + 2D view (main) + optional 3D — takes remaining vertical space */}
      <div
        className={`grid min-h-0 flex-1 grid-cols-1 gap-2 ${
          show3D ? "md:grid-cols-[280px_minmax(0,1fr)_minmax(0,1fr)]" : "md:grid-cols-[280px_minmax(0,1fr)]"
        }`}
      >
        <ShotList
          entries={entries}
          activeKind={kind}
          onRemove={onRemovePoint}
          currentFlangeNumber={currentFlangeNumber}
        />
        <div className={`min-h-[280px] ${between ? "opacity-40" : ""}`}>
          <Flange2D flange={flange} activeKind={kind} live={live} />
        </div>
        {show3D && (
          <div className={`min-h-[280px] ${between ? "opacity-40" : ""}`}>
            <Flange3D
              flange={flange}
              activeKind={kind}
              face1Points={points.filter((p) => p.kind === "face1")}
              face2Points={points.filter((p) => p.kind === "face2")}
              face3Points={points.filter((p) => p.kind === "face3")}
              boltPoints={points.filter((p) => p.kind === "bolt")}
              face1Sectors={face1?.sectors ?? new Array(24).fill(false)}
              face2Sectors={face2?.sectors ?? new Array(24).fill(false)}
              face3Sectors={face3?.sectors ?? new Array(24).fill(false)}
              boltSectors={bolt?.sectors ?? new Array(24).fill(false)}
              face1Coverage={face1?.coverage_deg ?? 0}
              face2Coverage={face2?.coverage_deg ?? 0}
              face3Coverage={face3?.coverage_deg ?? 0}
              boltCoverage={bolt?.coverage_deg ?? 0}
            />
          </div>
        )}
      </div>

      {/* Row 3: comparison table + FINALIZE with helper text */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <ComparisonTable face1={face1} face2={face2} face3={face3} bolt={bolt} flange={flange} />
        <div className="flex flex-col items-stretch justify-center gap-1">
          <button data-testid="spool-finalize-btn" onClick={() => onFinalize(false)}
            disabled={!ok || !job || between}
            className="btn-tac !min-h-[46px] px-6 text-sm" data-variant={ok && !between ? "amber" : undefined}>
            <span className="flex items-center justify-center gap-1">
              <CheckCircle size={16} weight="bold" /> FINALIZE
            </span>
          </button>
          {!between && !ok && missing.length > 0 && (
            <>
              <div
                data-testid="finalize-missing-hint"
                className="max-w-[220px] text-center text-[9px] uppercase tracking-widest text-[var(--amber)]"
                title={missing.join(" · ")}
              >
                Need: {missing.join(" · ")}
              </div>
              <button
                data-testid="spool-force-finalize-btn"
                onClick={() => onFinalize(true)}
                disabled={!job}
                className="btn-tac !min-h-[28px] px-3 text-[9px]"
                data-variant="danger"
                title="Save partial measurements as a spool point anyway"
              >
                FORCE SAVE
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// GSI-8 style shot list — shows each measured point in the running session
// in the same "point# · code · Hz · V · SD" layout as the Leica GSI files.
//
// Codes follow the DC Survey Field Guide convention:
//   • xx1 = Face 1 (raised face)      · e.g. Flange 1 → 011, Flange 2 → 021
//   • xx2 = Face 2 (front of large OD) · e.g. Flange 1 → 012
//   • xx3 = Face 3 (back of flange OD) · e.g. Flange 1 → 013
//   • xx4 = Bolt centre points         · e.g. Flange 1 → 014
const SHOT_CODE = {
  face1: { suffix: 1, label: "F1", color: "#ff5555" },
  face2: { suffix: 2, label: "F2", color: "#ffb000" },
  face3: { suffix: 3, label: "F3", color: "#00e5ff" },
  bolt:  { suffix: 4, label: "PCD", color: "#ff55ff" },
};

// Build the 3-digit Wilson field code for a given flange number + measurement
// kind. Flange numbers > 99 fall back to zero-padded 3-digit prefix.
export const codeFor = (flangeNumber, kind) => {
  const meta = SHOT_CODE[kind];
  if (!meta) return "000";
  const pad = flangeNumber < 100 ? 2 : 3;
  return `${String(flangeNumber).padStart(pad, "0")}${meta.suffix}`;
};

// Radians → "DDD°MM'SS\""
const radToDMS = (rad) => {
  if (rad === null || rad === undefined || Number.isNaN(rad)) return "—";
  let deg = (rad * 180) / Math.PI;
  deg = ((deg % 360) + 360) % 360;
  const d = Math.floor(deg);
  const mFloat = (deg - d) * 60;
  const m = Math.floor(mFloat);
  const s = Math.round((mFloat - m) * 60);
  return `${String(d).padStart(3, "0")}°${String(m).padStart(2, "0")}'${String(s).padStart(2, "0")}"`;
};

// Build the flat list of shot-list entries by merging the finalized history
// with the current live session. Each entry carries its own flange number so
// the ShotList can render everything continuously across a whole survey run.
const buildShotEntries = (history, currentFlangeNumber, livePoints) => {
  const entries = [];
  let seq = 0;
  for (const flange of history) {
    for (const p of flange.points || []) {
      seq += 1;
      entries.push({
        seq,
        flangeNumber: flange.flangeNumber,
        tag: flange.tag,
        kind: p.kind,
        x: p.x, y: p.y, z: p.z,
        hz: p.hz, v: p.v, sd: p.sd,
        finalized: true,
      });
    }
  }
  for (const p of livePoints || []) {
    seq += 1;
    entries.push({
      seq,
      flangeNumber: currentFlangeNumber || 1,
      tag: null,
      kind: p.kind,
      x: p.x, y: p.y, z: p.z,
      hz: p.hz, v: p.v, sd: p.sd,
      finalized: false,
    });
  }
  return entries;
};

const ShotList = ({ entries, activeKind, onRemove, currentFlangeNumber }) => {
  // Detect flange group boundaries so we can render a divider row between them.
  let prevFlange = null;
  // Compute live-session index (matches backend session.points[] 0-based
  // order) inline so it survives the object-spread in withGroups.
  let liveCounter = -1;
  const withGroups = entries.map((e) => {
    const isNewGroup = e.flangeNumber !== prevFlange;
    prevFlange = e.flangeNumber;
    let liveIdx = -1;
    if (!e.finalized) {
      liveCounter += 1;
      liveIdx = liveCounter;
    }
    return { ...e, isNewGroup, liveIdx };
  });
  return (
    <div
      data-testid="spool-shot-list"
      className="flex h-full min-h-[280px] flex-col rounded-sm border border-[var(--border)] bg-[var(--surface-2)]"
    >
      <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
          Shot list
        </span>
        <span className="readout tabular-nums text-[10px] text-[var(--neon)]">
          {entries.length} pts
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {entries.length === 0 ? (
          <div className="p-4 text-center text-[10px] uppercase tracking-widest text-[var(--text-mute)]">
            No shots yet. Hit MEASURE.
          </div>
        ) : (
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 z-10 bg-[var(--surface)] text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
              <tr>
                <th className="px-2 py-1 text-left font-normal">Pt</th>
                <th className="px-1 py-1 text-left font-normal">Code</th>
                <th className="px-1 py-1 text-right font-normal">X (m)</th>
                <th className="px-1 py-1 text-right font-normal">Y (m)</th>
                <th className="px-1 py-1 text-right font-normal">Z (m)</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody className="readout tabular-nums">
              {withGroups.map((e, i) => {
                const meta = SHOT_CODE[e.kind] || { label: "??", color: "#94a3b8" };
                const wilson = codeFor(e.flangeNumber, e.kind);
                const isCurrentFlange = !e.finalized;
                const isActive = isCurrentFlange && e.kind === activeKind;
                // Index within this flange group — recompute cheaply by
                // scanning back to the start of the group in the source list.
                const groupStart = withGroups.findIndex((x) => x.flangeNumber === e.flangeNumber);
                const withinGroup = i - groupStart + 1;
                // Current-session shots keep their index within the live
                // session (0-based, matches backend for delete).
                const liveIdx = e.liveIdx;
                return (
                  <React.Fragment key={i}>
                    {e.isNewGroup && (
                      <tr className="bg-[var(--bg)] text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
                        <td colSpan={6} className="border-y border-[var(--border)] px-2 py-1">
                          <div className="flex items-center justify-between">
                            <span>
                              — Flange #{e.flangeNumber}
                              {e.tag ? ` · ${e.tag}` : ""} —
                            </span>
                            {!e.finalized && (
                              <span className="text-[var(--neon)]">LIVE</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr
                      data-testid={`shot-row-${i}`}
                      className={`border-t border-[var(--border)] ${
                        isActive ? "bg-[var(--surface)]" : ""
                      } ${e.finalized ? "opacity-70" : ""}`}
                    >
                      <td className="px-2 py-1 text-[var(--text-dim)]">
                        {String(withinGroup).padStart(3, "0")}
                      </td>
                      <td className="px-1 py-1" style={{ color: meta.color }}>
                        <span className="readout tabular-nums font-semibold">{wilson}</span>
                        <span className="ml-1 text-[9px] text-[var(--text-mute)]">
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-1 py-1 text-right text-[var(--text)]">
                        {e.x != null ? e.x.toFixed(3) : "—"}
                      </td>
                      <td className="px-1 py-1 text-right text-[var(--text)]">
                        {e.y != null ? e.y.toFixed(3) : "—"}
                      </td>
                      <td className="px-1 py-1 text-right text-[var(--text)]">
                        {e.z != null ? e.z.toFixed(3) : "—"}
                      </td>
                      <td className="px-1 py-1 text-right">
                        {!e.finalized && (
                          <button
                            data-testid={`shot-delete-${liveIdx}`}
                            onClick={() => liveIdx >= 0 && onRemove(liveIdx)}
                            title="Remove this shot"
                            className="text-[var(--text-mute)] hover:text-[var(--danger)]"
                          >
                            <Trash size={11} weight="bold" />
                          </button>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------- Next-flange confirmation dialog ----------
//
// Modal shown after a FINALIZE. The surveyor never leaves the SPOOL view — the
// dialog just floats over it. They confirm the incoming flange's design and TP
// number (which map to `tag` + `flange_number` on the backend), then the panel
// starts a fresh session in-place.
const NextFlangeDialog = ({ draft, standards, onChange, onConfirm, onFinishRun }) => {
  const [matches, setMatches] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [gaskets, setGaskets] = useState([]);
  const [selectedFlange, setSelectedFlange] = useState(null);

  // Refresh matches whenever filters change.
  useEffect(() => {
    if (!draft.selStd) return;
    const params = { standard: draft.selStd };
    if (draft.selSize) params.size = draft.selSize;
    if (draft.selClass) params.klass = draft.selClass;
    if (draft.selGasket) params.gasket = draft.selGasket;
    flanges.list(params).then((rows) => {
      setMatches(rows);
      setSizes(Array.from(new Set(rows.map((r) => r.size).filter(Boolean))).sort(
        (a, b) => parseFloat(a) - parseFloat(b) || a.localeCompare(b)
      ));
      setClasses(Array.from(new Set(rows.map((r) => r.class).filter(Boolean))));
      setGaskets(Array.from(new Set(rows.map((r) => r.gasket).filter(Boolean))));
      // Pre-select the previous flange if it's still in the match list.
      const stillThere = rows.find((r) => r.id === draft.flangeId);
      setSelectedFlange(stillThere || null);
    }).catch(() => setMatches([]));
  }, [draft.selStd, draft.selSize, draft.selClass, draft.selGasket, draft.flangeId]);

  const patch = (p) => onChange({ ...draft, ...p });

  const confirm = () => {
    onConfirm({
      ...draft,
      flangeId: selectedFlange?.id ?? draft.flangeId,
    });
  };

  const canConfirm = !!(selectedFlange || draft.flangeId) && Number(draft.flangeNumber) > 0;

  return (
    <div
      data-testid="next-flange-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-3xl rounded-md border border-[var(--neon)] bg-[var(--surface)] shadow-[0_0_30px_rgba(0,255,153,0.15)]">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div>
            <div className="flex items-center gap-2 text-[var(--neon)]">
              <CheckCircle size={18} weight="bold" />
              <span className="text-sm font-bold tracking-widest">
                FLANGE #{draft.justFinishedNumber} SAVED
              </span>
            </div>
            <div className="mt-0.5 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
              Confirm details for flange #{draft.flangeNumber}, or finish the run.
            </div>
          </div>
          <button
            data-testid="finish-survey-btn"
            onClick={onFinishRun}
            className="btn-tac !min-h-[36px] px-3 text-[10px]"
          >
            FINISH RUN
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Selector testid="next-standard-select" label="Standard"
              value={draft.selStd}
              onChange={(v) => patch({ selStd: v, selSize: "", selClass: "", selGasket: "" })}
              options={standards} />
            <Selector testid="next-size-select" label="Size (in)"
              value={draft.selSize}
              onChange={(v) => patch({ selSize: v })}
              options={sizes} />
            <Selector testid="next-class-select" label="Class"
              value={draft.selClass}
              onChange={(v) => patch({ selClass: v })}
              options={classes} />
            <Selector testid="next-gasket-select" label="Gasket"
              value={draft.selGasket}
              onChange={(v) => patch({ selGasket: v })}
              options={gaskets} />
          </div>

          <div className="max-h-[180px] overflow-auto rounded-sm border border-[var(--border)] bg-[var(--bg)] p-1">
            <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
              {matches.length} matches — click one
            </div>
            {matches.slice(0, 40).map((f) => {
              const isSel = selectedFlange?.id === f.id;
              return (
                <button
                  key={f.id}
                  data-testid={`next-flange-option-${f.id}`}
                  onClick={() => setSelectedFlange(f)}
                  className={`w-full rounded-sm px-2 py-1 text-left text-[10px] ${
                    isSel
                      ? "bg-[var(--neon)] text-black"
                      : "text-[var(--text-dim)] hover:bg-[var(--surface-2)]"
                  }`}
                >
                  <span className="readout tabular-nums font-semibold">#{f.id}</span>
                  {" · "}{f.standard} {f.size}" cl.{f.class}
                  {f.gasket ? ` ${f.gasket}` : ""}
                  {" · "}OD Ø{fmt(f.diam_o, 1)} · PCD Ø{fmt(f.diam_bc, 1)}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                Flange #
              </span>
              <input
                data-testid="next-flange-number-input"
                type="number" min={1}
                value={draft.flangeNumber}
                onChange={(e) => patch({ flangeNumber: e.target.value })}
                className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                TP number / Tag
              </span>
              <input
                data-testid="next-tp-input"
                type="text" placeholder="e.g. TP-01 / P-201"
                value={draft.tag}
                onChange={(e) => patch({ tag: e.target.value })}
                className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                  Axial offset (mm)
                </span>
                <input
                  data-testid="next-offset-axial-input"
                  type="number"
                  value={draft.offsetAxial}
                  onChange={(e) => patch({ offsetAxial: e.target.value })}
                  className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
                  Radial (mm)
                </span>
                <input
                  data-testid="next-offset-radial-input"
                  type="number"
                  value={draft.offsetRadial}
                  onChange={(e) => patch({ offsetRadial: e.target.value })}
                  className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-4 py-3">
          <span className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            Codes will be assigned:{" "}
            <span className="readout tabular-nums text-[var(--text)]">
              {codeFor(draft.flangeNumber || 1, "face1")}
              /{codeFor(draft.flangeNumber || 1, "face2")}
              /{codeFor(draft.flangeNumber || 1, "face3")}
              /{codeFor(draft.flangeNumber || 1, "bolt")}
            </span>
          </span>
          <button
            data-testid="confirm-next-flange-btn"
            onClick={confirm}
            disabled={!canConfirm}
            className="btn-tac !min-h-[46px] px-6 text-sm"
            data-variant="primary"
          >
            <span className="flex items-center gap-1">
              <Target size={16} weight="bold" />
              START FLANGE #{draft.flangeNumber}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};






const ModeBtn = ({ testid, active, onClick, count, coverage, label, sub, code }) => {
  const covered = coverage >= 180;
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`rounded-sm border px-3 py-2 text-left transition-colors ${
        active
          ? "border-[var(--neon)] bg-[var(--surface)] text-[var(--text)]"
          : "border-[var(--border)] bg-[var(--surface-2)] text-[var(--text-dim)] hover:border-[var(--border-strong)]"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-widest font-semibold">
          {label}
          {code && (
            <span className="ml-1 readout tabular-nums text-[10px] text-[var(--text-mute)]">
              {code}
            </span>
          )}
        </span>
        <span
          className={`text-[10px] readout tabular-nums ${
            covered
              ? "text-[var(--neon)]"
              : count > 0
                ? "text-[var(--amber)]"
                : "text-[var(--text-mute)]"
          }`}
        >
          {Math.round(coverage)}° · {count}pt
        </span>
      </div>
      {sub && (
        <div className="mt-0.5 text-[9px] uppercase tracking-widest text-[var(--text-mute)]">
          {sub}
        </div>
      )}
    </button>
  );
};

const ComparisonTable = ({ face1, face2, face3, bolt, flange }) => {
  const rows = [
    { key: "face1", label: "Face 1 · Raised",  blk: face1, nominal: flange?.rf_dia, testid: "cmp-face1" },
    { key: "face2", label: "Face 2 · Front OD", blk: face2, nominal: flange?.od,     testid: "cmp-face2" },
    { key: "face3", label: "Face 3 · Back OD",  blk: face3, nominal: flange?.od,     testid: "cmp-face3" },
    { key: "bolt",  label: "Bolt PCD",           blk: bolt,  nominal: flange?.pcd,    testid: "cmp-pcd" },
  ];
  return (
    <div
      data-testid="comparison-table"
      className="overflow-hidden rounded-sm border border-[var(--border)] bg-[var(--surface-2)]"
    >
      <table className="w-full text-xs">
        <thead className="bg-[var(--surface)] text-[var(--text-dim)]">
          <tr>
            <Th>Feature</Th>
            <Th>Design (mm)</Th>
            <Th>Measured (mm)</Th>
            <Th>Δ (mm)</Th>
            <Th>Δ%</Th>
            <Th>Coverage</Th>
            <Th>Pts · RMS</Th>
          </tr>
        </thead>
        <tbody className="readout tabular-nums">
          {rows.map((r) => {
            const b = r.blk;
            const measured = b?.diameter_measured_mm ?? null;
            const dev = b?.diameter_deviation_mm ?? null;
            const cov = b?.coverage_deg ?? 0;
            const npts = b?.n_points ?? 0;
            const rms = b?.residual_rms_mm;
            const devColor =
              dev === null || dev === undefined
                ? "text-[var(--text-dim)]"
                : Math.abs(dev) < 2
                  ? "text-[var(--neon)]"
                  : Math.abs(dev) < 10
                    ? "text-[var(--amber)]"
                    : "text-[var(--danger)]";
            const covColor = cov >= 180
              ? "text-[var(--neon)]"
              : cov > 0
                ? "text-[var(--amber)]"
                : "text-[var(--text-mute)]";
            return (
              <tr key={r.key} data-testid={r.testid} className="border-t border-[var(--border)]">
                <Td className="font-semibold uppercase tracking-widest text-[var(--text)] text-[11px]">
                  {r.label}
                </Td>
                <Td className="text-[var(--text-dim)]">
                  {r.nominal !== null && r.nominal !== undefined ? r.nominal.toFixed(2) : "—"}
                </Td>
                <Td className="text-[var(--text)] font-semibold">
                  {measured !== null && measured !== undefined ? measured.toFixed(2) : "—"}
                </Td>
                <Td className={devColor}>
                  {dev !== null && dev !== undefined ? (dev >= 0 ? "+" : "") + dev.toFixed(2) : "—"}
                </Td>
                <Td className={devColor}>
                  {dev !== null && dev !== undefined && r.nominal
                    ? ((dev / r.nominal) * 100).toFixed(3) + "%"
                    : "—"}
                </Td>
                <Td className={covColor}>{Math.round(cov)}°</Td>
                <Td className="text-[var(--text-dim)]">
                  {npts} · {rms !== null && rms !== undefined ? rms.toFixed(2) : "—"}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Th = ({ children }) => (
  <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest font-normal">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-2 py-1.5 ${className}`}>{children}</td>
);

// ---------- Coverage radial visualization (unused now, kept for reference) ----------

const CoverageRing = ({ sectors, coverageDeg }) => {
  const R_OUT = 90, R_IN = 65;
  const step = 360 / sectors.length; // 15°
  const sectorPath = (i) => {
    const a1 = (i * step - 90) * (Math.PI / 180);
    const a2 = ((i + 1) * step - 90) * (Math.PI / 180);
    const x1o = Math.cos(a1) * R_OUT, y1o = Math.sin(a1) * R_OUT;
    const x2o = Math.cos(a2) * R_OUT, y2o = Math.sin(a2) * R_OUT;
    const x1i = Math.cos(a1) * R_IN, y1i = Math.sin(a1) * R_IN;
    const x2i = Math.cos(a2) * R_IN, y2i = Math.sin(a2) * R_IN;
    return `M ${x1o} ${y1o} A ${R_OUT} ${R_OUT} 0 0 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${R_IN} ${R_IN} 0 0 0 ${x1i} ${y1i} Z`;
  };

  const good = coverageDeg >= 180;

  return (
    <div className="relative" data-testid="coverage-ring">
      <svg viewBox="-110 -110 220 220" className="w-full max-w-[220px]">
        {/* Sectors */}
        {sectors.map((on, i) => (
          <path
            key={i}
            d={sectorPath(i)}
            fill={on ? (good ? "#00ff41" : "#ffb000") : "#1a1d24"}
            stroke="#0a0a0a"
            strokeWidth="1"
            opacity={on ? 0.85 : 1}
            filter={on ? "url(#sectorGlow)" : undefined}
          />
        ))}
        {/* Inner crosshair */}
        <circle cx="0" cy="0" r="50" fill="none" stroke="#2a2f3a" strokeWidth="1" />
        <line x1="-10" y1="0" x2="10" y2="0" stroke="#3b4252" />
        <line x1="0" y1="-10" x2="0" y2="10" stroke="#3b4252" />
        {/* Cardinal marks */}
        <text x="0" y="-100" fontSize="9" textAnchor="middle" fill="#3b4252" fontFamily="JetBrains Mono">0°</text>
        <text x="100" y="4" fontSize="9" textAnchor="middle" fill="#3b4252" fontFamily="JetBrains Mono">90</text>
        <text x="0" y="108" fontSize="9" textAnchor="middle" fill="#3b4252" fontFamily="JetBrains Mono">180</text>
        <text x="-100" y="4" fontSize="9" textAnchor="middle" fill="#3b4252" fontFamily="JetBrains Mono">270</text>
        {/* Center label */}
        <text x="0" y="-2" fontSize="20" fontWeight="bold" textAnchor="middle"
              fill={good ? "#00ff41" : "#ffb000"} fontFamily="JetBrains Mono">
          {Math.round(coverageDeg)}°
        </text>
        <text x="0" y="16" fontSize="8" textAnchor="middle" fill="#94a3b8" fontFamily="Rajdhani" letterSpacing="2">
          COVERAGE
        </text>
        <defs>
          <filter id="sectorGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
    </div>
  );
};

const StatCard = ({ testid, label, value, unit, color, glow }) => (
  <div
    data-testid={testid}
    className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2"
  >
    <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">{label}</div>
    <div className={`readout mt-1 text-2xl font-bold tabular-nums ${color} ${glow || ""}`}>
      {value}
      <span className="ml-1 text-xs text-[var(--text-dim)]">{unit}</span>
    </div>
  </div>
);
